import { ReferenceType } from '@modules/client';
import pluralize from 'pluralize';
import {
  apiReferenceBasePath,
  classesBasePath,
  datatypesBasePath,
  enumsBasePath,
  globalsBasePath,
  librariesBasePath,
  referenceTypePathMapping,
} from '../../constants/apiReferenceConstants';

// process.env.references is set in next.config.js and it's an object.
// for test, process.env.references is set in setupEnvs.js and it's a string.
let allReferences =
  typeof process.env.references === 'string'
    ? JSON.parse(process.env.references)
    : process.env.references;

let allClassesSet = new Set(allReferences.Class);
let allDataTypeSet = new Set<string>(allReferences.DataType);
let allEnumSet = new Set<string>(allReferences.Enum);
let allGlobalSet = new Set<string>(allReferences.Global);
let allLibrarySet = new Set<string>(allReferences.Library);

export function updateReferences(newReferences: References) {
  if (!newReferences) {
    return;
  }

  allReferences = newReferences;
  allClassesSet = new Set(allReferences.Class);
  allDataTypeSet = new Set<string>(allReferences.DataType);
  allEnumSet = new Set<string>(allReferences.Enum);
  allGlobalSet = new Set<string>(allReferences.Global);
  allLibrarySet = new Set<string>(allReferences.Library);
}

export function getReferences(): References {
  return allReferences;
}

// Create reverse lookup map: member name -> file name
export function getGlobalMemberToFileMap(
  globalByFile: Record<string, string[]>,
): Record<string, string> {
  const map: Record<string, string> = {};
  Object.entries(globalByFile).forEach(([fileName, members]) => {
    members.forEach((member) => {
      map[member] = fileName;
    });
  });
  return map;
}

export const scriptingArticleBasePath = '/luau';

export const isSupportedReferenceMember = (memberName: string, referenceType: ReferenceType) => {
  let referenceTypeSet;
  switch (referenceType) {
    case ReferenceType.CLASS:
      referenceTypeSet = allClassesSet;
      break;
    case ReferenceType.DATATYPE:
      referenceTypeSet = allDataTypeSet;
      break;
    case ReferenceType.ENUM:
      referenceTypeSet = allEnumSet;
      break;
    case ReferenceType.GLOBAL:
      referenceTypeSet = allGlobalSet;
      break;
    case ReferenceType.LIBRARY:
      referenceTypeSet = allLibrarySet;
      break;
    default:
      throw Error(`Invalid referenceType: ${referenceType}`);
  }
  return referenceTypeSet.has(memberName);
};

export const getAPIReferencePath = (slug: string, type: ReferenceType) =>
  `${apiReferenceBasePath}/${referenceTypePathMapping[type]}/${slug}`;
export const getScriptingArticlePath = (typeName: string): string => {
  let slug: string;
  const namePlural = pluralize(typeName).toLowerCase();
  switch (namePlural) {
    case 'nil':
    case 'nils':
      slug = 'nil';
      break;
    case 'tables':
    case 'arrays':
      slug = 'tables';
      break;
    default:
      slug = namePlural;
      break;
  }
  return `${scriptingArticleBasePath}/${slug}`;
};

export const ApiTypes = ['Class', 'Datatype', 'Enum', 'Global', 'Library'];
export type ApiType = (typeof ApiTypes)[number];
export const isApiType = (candidate: string): candidate is ApiType =>
  ['Class', 'Datatype', 'Enum', 'Global', 'Library'].includes(candidate);

export const getApiBasePath = (apiType: ApiType | string, apiName: string, apiMember = '') => {
  // returns the API base path only if apiName exists
  // supported constants located here:
  // app\modules\apiReference\page\sharedUI\constants\linkableTypeReferenceConstants.ts
  if (apiType === 'Class' && isSupportedReferenceMember(apiName, ReferenceType.CLASS)) {
    return classesBasePath;
  }
  if (apiType === 'Datatype' && isSupportedReferenceMember(apiName, ReferenceType.DATATYPE)) {
    return datatypesBasePath;
  }
  if (apiType === 'Enum' && isSupportedReferenceMember(apiName, ReferenceType.ENUM)) {
    return enumsBasePath;
  }
  if (apiType === 'Global' && isSupportedReferenceMember(apiMember, ReferenceType.GLOBAL)) {
    return globalsBasePath;
  }
  if (apiType === 'Library' && isSupportedReferenceMember(apiName, ReferenceType.LIBRARY)) {
    return librariesBasePath;
  }
  return null;
};

export interface CompoundType {
  outerType?: string;
  innerTypes?: string[];
  multipleOrTypes?: string[];
}

export const compoundTypeRegex = /^(?<outerType>.*?)<(?<innerTypes>.*)>$/;
// Array<BundleType> => { outerType: 'Array', genericType: ['BundleType'] }

export const matchesCompoundType = (type: string): RegExpExecArray | null => {
  const regex = new RegExp(compoundTypeRegex);
  return regex.exec(type);
};

export const parseAliasForCompoundTypes = ({
  type,
  compoundTypeMatch,
}: {
  type?: string;
  compoundTypeMatch?: RegExpExecArray | null;
}): CompoundType => {
  // type: string like 'Array<BundleType>' or 'Enum.Material' or 'number'
  if (!compoundTypeMatch && !type)
    throw new Error(`type (${type}) or compoundTypeMatch (${compoundTypeMatch}) must be provided`);
  const matches = type ? matchesCompoundType(type) : compoundTypeMatch;

  return {
    outerType: (matches && (matches as RegExpExecArray).groups?.outerType) ?? '',
    innerTypes:
      (matches &&
        (matches as RegExpExecArray).groups?.innerTypes?.split(',').map((x) => x.trim())) ??
      [],
    multipleOrTypes: (type ?? '').split('|').map((t) => t.trim()),
  };
};
